// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartLineContainer, { CartLineContainerStyles } from "@guestsupply/App/Components/Cart/CartLineContainer";
import Notify from "@guestsupply/App/Components/Notify";
import TippyPopup from "@guestsupply/App/Components/Tippy/TippyPopup";
import setShipFromAltDCModalIsOpen from "@guestsupply/App/Handlers/BackOrders/SetShipFromAltDCModalIsOpen";
import updatePoNumbers from "@guestsupply/App/Handlers/PoNumbers/UpdatePoNumbers";
import xUseWidgetBadges from "@guestsupply/App/Hooks/xUseWidgetBadges";
import TextField from "@guestsupply/MobiusOverrides/TextField/TextField";
import ProductAvailability, {
    AdditionalAvailabilityInfoModel,
    ProductAvailabilityStyles,
} from "@guestsupply/Overrides/Components/ProductAvailability";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@guestsupply/Overrides/Components/ProductPartNumbers";
import { poRegex, shippingWeightUnit } from "@guestsupply/XDL/constants";
import More from "@guestsupply/XDL/Icons/More";
import Plus from "@guestsupply/XDL/Icons/Plus";
import ShippingWeight from "@guestsupply/XDL/Icons/ShippingWeight";
import Stack from "@guestsupply/XDL/Icons/Stack";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal from "@insite/mobius/Modal";
import { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Tippy from "@tippyjs/react";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
/* eslint-disable spire/fenced-imports */
import ProductBadges from "@guestsupply/App/Components/Common/ProductBadges";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import Tooltip, { TooltipPresentationProps } from "@guestsupply/MobiusOverrides/Tooltip/Tooltip";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { hideAll, inlinePositioning } from "tippy.js";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    editable: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    // XNG-CHANGES
    showGroups?: boolean;
    showEligibleIcon?: boolean;
}
interface PoList {
    [key: string]: {
        original: string;
        temp: string;
        removed: boolean;
    };
}
const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
    isAuthenticated: xIsAuthenticated(state),
    isOrderConfirmationPage: getCurrentPage(state).type === "OrderConfirmationPage",
});

const mapDispatchToProps = {
    updateCartLine,
    removeCartLine,
    updatePoNumbers,
    setShipFromAltDCModalIsOpen,
    loadCurrentCart,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    cartLineNotes?: CartLineNotesStyles;
    price?: ProductPriceStyles;
    quantity?: CartLineQuantityStyles;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoContainer?: GridContainerProps;
    productDescriptionAndPartNumbersGridItem?: GridItemProps;
    productDescriptionAndPartNumbersContainer?: GridContainerProps;
    productBrand?: ProductBrandStyles;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    orderLineNotesGridItem?: GridItemProps;
    notesHeadingAndText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeValueText?: TypographyPresentationProps;
    costCodeLabelText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionNameText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    qtyHeadingAndText?: SmallHeadingAndTextStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    subtotalWithoutVatText?: SmallHeadingAndTextStyles;
    vatAmount?: SmallHeadingAndTextStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    // XNG-CHANGES
    smallHeadingStyle?: SmallHeadingAndTextStyles;
    lineContainerStyles?: CartLineContainerStyles;
    shippingWeightWrapper?: InjectableCss;
    headlineTypography?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    totalFreightChargesLink?: LinkPresentationProps;
    surchargesTooltip?: TooltipPresentationProps;
    poContainer?: InjectableCss;
    applyToAllLink?: LinkPresentationProps;
    editPurchaseOrderLink?: LinkPresentationProps;
    removeLink?: LinkPresentationProps;
    tippyContentWrapper?: InjectableCss;
    poListWrapper?: InjectableCss;
    poListLink?: LinkPresentationProps;
    moreIconLink?: LinkPresentationProps;
    poModalWrapper?: InjectableCss;
    polineWrapper?: InjectableCss;
    removePoWrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    poNumberWrapper?: InjectableCss;
    poNumber?: SmallHeadingAndTextStyles;
    altDcLink?: LinkPresentationProps;
    notify?: TypographyProps;
    icon?: IconProps;
    tooltipStyle?: TooltipPresentationProps;
    productImageBadgesStyle?: ProductImageBadgesStyles;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {};

const enterKey = 13;

const CartLineCardExpanded = ({
    cart,
    cartLine,
    showSavingsAmount,
    showSavingsPercent,
    promotions,
    editable,
    enableVat,
    vatPriceDisplay,
    showInvalidPriceMessage,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    // XNG-CHANGES
    showGroups = false,
    updateCartLine,
    removeCartLine,
    updatePoNumbers,
    setShipFromAltDCModalIsOpen,
    showEligibleIcon,
    isAuthenticated,
    loadCurrentCart,
    isOrderConfirmationPage,
}: Props) => {
    const [styles] = useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));
    const [pOModalOpen, setPoModalOpen] = useState(false);
    const [postalCode, setPostalCode] = useState(cartLine?.properties?.poNumber ?? "");
    const [poLineList, setPoLineList] = useState<PoList>({});
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [poTobeRemoved, setPoTobeRemoved] = useState("");
    const [isPoLoading, setIsPoLoading] = useState(false);
    const [isPoModalLoading, setIsPoModalLoading] = useState(false);

    const toaster = useContext(ToasterContext);

    const cartLines = cart.cartLines;

    // XNG-CHANGES
    const additionalAvailabilityInfo =
        cartLine?.properties?.additionalAvailabilityInfo &&
        JSON.parse(cartLine?.properties?.additionalAvailabilityInfo);

    useEffect(() => {
        const poList: PoList = {};

        cartLines?.forEach(line => {
            if (line.properties.poNumber) {
                poList[line.properties.poNumber] = {
                    original: line.properties.poNumber,
                    temp: line.properties.poNumber,
                    removed: false,
                };
            }
        });
        setPoLineList(poList);
    }, [cartLines]);

    useEffect(() => {
        setPostalCode(cartLine?.properties?.poNumber);
    }, [cartLine]);

    // XNG-CHANGES
    const { ootbBadgesAndTags, customBadgesAndTags } = xUseWidgetBadges({
        productContext: { product: cartLine, productInfo: {} } as unknown as ProductContextModel,
        showTextBadges: false,
        showImageBadges: true,
    });

    const calculateSavedValue =
        cartLine.pricing &&
        cartLine.pricing.unitListPriceDisplay.charAt(0) +
            ((cartLine.pricing.unitListPrice - cartLine.pricing.unitNetPrice) * (cartLine.qtyOrdered ?? 0)).toFixed(2);

    const errorMessages: React.ReactNode[] = [];

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    const applyToAllClickHandler = (PoNumber: string) => {
        if (!cartLines || !cartLines.length) {
            return;
        }
        setIsPoLoading(true);
        const lines = cartLines?.map(line => {
            return {
                cartLineId: line.id,
                PoNumber: PoNumber ?? "",
            };
        });
        updatePoNumbers({
            lines,
            onError: () => {
                toaster.addToast({
                    body: translate("There was an error processing your request."),
                    messageType: "danger",
                });
            },
            onComplete: () => {
                setIsPoLoading(false);
                hideAll();
                loadCurrentCart();
            },
        });
    };

    // XNG-CHANGES : GSS-590
    const isSaveChangesEnabled = Object.keys(poLineList).some(
        poLine => poLineList[poLine].original !== poLineList[poLine].temp || poLineList[poLine].removed,
    );

    const isShowPOEnabled = Object.keys(poLineList).some(poLine => !!poLineList[poLine]?.original?.length);

    const handleSaveChangesClick = () => {
        setIsPoModalLoading(true);
        if (cartLines) {
            hideAll();
            const lines = cartLines?.map(line => {
                return {
                    cartLineId: line.id,
                    PoNumber: poLineList[line?.properties?.poNumber ?? ""]?.removed
                        ? ""
                        : poLineList[line?.properties?.poNumber ?? ""]?.temp ?? "",
                };
            });
            updatePoNumbers({
                lines,
                onError: () => {
                    toaster.addToast({
                        body: translate("There was an error processing your request."),
                        messageType: "danger",
                    });
                },
                onComplete: () => {
                    setIsPoModalLoading(false);
                    setPoModalOpen(false);
                    loadCurrentCart();
                },
            });
        }
    };

    const handleRemoveClick = (po: string) => {
        setShowRemoveModal(true);
        setPoTobeRemoved(po);
    };
    const handleEditPOClick = () => {
        hideAll();
        setPoModalOpen(true);
    };
    const handlePOLineListClick = (poItem: string) => {
        if (cartLine?.properties?.poNumber !== poLineList[poItem].original) {
            setIsPoLoading(true);
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        ...cartLine.properties,
                        poNumber: poLineList[poItem].original,
                    },
                },

                onComplete: () => {
                    setIsPoLoading(false);
                    hideAll();
                },
            });
        }
    };
    const handleCancelClick = () => {
        setPoModalOpen(false);
        if (poTobeRemoved?.length) {
            setPoLineList({
                ...poLineList,
                [poLineList[poTobeRemoved].original]: {
                    ...poLineList[poTobeRemoved],
                    removed: false,
                },
            });
        }
    };
    const handleYesClick = () => {
        setShowRemoveModal(false);
        setPoLineList({
            ...poLineList,
            [poLineList[poTobeRemoved].original]: {
                ...poLineList[poTobeRemoved],
                removed: true,
            },
        });
    };

    const handleShipFromPrimaryDC = () => {
        if (isOrderConfirmationPage) {
            return;
        }
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    removeSelectedAltWarehouse: "true",
                },
            },
        });
    };

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0 && removeCartLine) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine &&
                    updateCartLine({
                        cartLine: {
                            ...cartLine,
                            qtyOrdered,
                        },
                    });
            }
        }
    };
    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const validatePOInput = poRegex.test(event.currentTarget.value);
        if (validatePOInput || !event.target.value?.length) {
            setPostalCode(event.target.value);
        }
    };
    // XNG-CHANGES : GSS-590 : load cart after po changes
    const onBlurHandler = () => {
        if (cartLine?.properties?.poNumber !== postalCode) {
            setIsPoLoading(true);
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        ...cartLine.properties,
                        poNumber: postalCode,
                    },
                },
                onComplete: () => {
                    setIsPoLoading(false);
                    hideAll();
                },
            });
        }
    };

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === enterKey) {
            event.stopPropagation();
            hideAll();
        }
    };
    const handlePOChange = (event: React.ChangeEvent<HTMLInputElement>, poItem: string) => {
        const validatePOInput = poRegex.test(event.currentTarget.value);
        if (validatePOInput || !event.target.value?.length) {
            setPoLineList(prevValue => {
                return {
                    ...prevValue,
                    [poLineList[poItem].original]: {
                        ...poLineList[poItem],
                        temp: event.target.value,
                    },
                };
            });
        }
    };
    return (
        <>
            <CartLineContainer
                extendedStyles={styles.lineContainerStyles}
                className={`${showGroups && "group-cartline"}`}
            >
                <CartLineContainer.Image>
                    <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                </CartLineContainer.Image>

                <CartLineContainer.Description>
                    {/* {cartLine.brand && <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />} */}
                    <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                    {!cartLine.isFixedConfiguration &&
                        cartLine.sectionOptions!.length > 0 &&
                        cartLine.sectionOptions!.map(option => (
                            <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                {`${option.sectionName}:${option.optionName}`}
                            </Typography>
                        ))}
                    <ProductPartNumbers
                        productNumber={cartLine.erpNumber}
                        customerProductNumber={cartLine.customerName}
                        manufacturerItem={cartLine.manufacturerItem}
                        extendedStyles={styles.productPartNumbers}
                        showShippingWeight
                        shippingWeight={cartLine?.properties?.shippingWeight}
                        showEligibleIcon={showGroups && showEligibleIcon && isAuthenticated}
                    />
                    {/* XNG-CHANGES */}
                    {/* {cartLine?.properties?.shippingWeight ? (
                        <StyledWrapper {...styles.shippingWeightWrapper}>
                            <SmallHeadingAndText
                                heading={translate("Shipping Weight")}
                                text={`${cartLine?.properties?.shippingWeight} ${
                                    cartLine?.properties?.shippingWeight?.toLowerCase() !== "n/a" && shippingWeightUnit
                                }`}
                                extendedStyles={styles.smallHeadingStyle}
                            />
                            {showGroups && showEligibleIcon && isAuthenticated && <Icon {...styles.icon} />}
                        </StyledWrapper>
                    ) : null} */}
                    {/* XNG-CHANGES */}
                    {cartLine.properties?.packDescription && (
                        <SmallHeadingAndText
                            heading={translate("Case Pack")}
                            text={cartLine.properties?.packDescription}
                            extendedStyles={styles.smallHeadingStyle}
                        />
                    )}
                    <ProductBadges ootbBadgesAndTags={ootbBadgesAndTags} customBadgesAndTags={customBadgesAndTags} />
                </CartLineContainer.Description>
                <CartLineContainer.Price>
                    <Typography {...styles.headlineTypography}>{translate("Price/Unit")}</Typography>
                    <ProductPrice
                        product={cartLine}
                        showSavings={true}
                        showSavingsAmount={showSavingsAmount}
                        showSavingsPercent={showSavingsPercent}
                        showInvalidPriceMessage={showInvalidPriceMessage}
                        extendedStyles={styles.price}
                        // XNG-CHANGES
                        showLabel={false}
                    />

                    {promotions?.map(promotion => (
                        <SmallHeadingAndText
                            key={promotion.id}
                            heading={translate("You Saved")}
                            text={promotion.amountDisplay}
                            extendedStyles={styles.smallHeadingStyle}
                        />
                    ))}
                    {promotions?.length === 0 &&
                        cartLine?.pricing &&
                        cartLine?.pricing?.unitNetPrice < cartLine?.pricing?.unitListPrice && (
                            <SmallHeadingAndText
                                heading={translate("You Saved")}
                                text={calculateSavedValue ?? 0}
                                extendedStyles={styles.smallHeadingStyle}
                            />
                        )}
                    {cartLine?.properties?.totalFreightCharges && cartLine.properties.totalFreightCharges !== "$0.00" && (
                        <Tooltip
                            {...styles.surchargesTooltip}
                            text={siteMessage("GUEST_Surcharges_Icon_Hover", "This product has surcharges") as string}
                            triggerComponent={
                                <Link {...styles.totalFreightChargesLink} data-test-selector="totalFreightCharges">
                                    {cartLine?.properties?.totalFreightCharges}
                                </Link>
                            }
                        />
                    )}
                    {!cartLine.quoteRequired && (
                        <ProductAvailability
                            productId={cartLine.productId!}
                            availability={cartLine.availability!}
                            unitOfMeasure={cartLine.unitOfMeasure}
                            trackInventory={cartLine.trackInventory}
                            failedToLoadInventory={cart.failedToGetRealTimeInventory}
                            altDc={cartLine.properties.availableAltWarehouse}
                            altDcSelected={cartLine.properties.altWarehouseInfo}
                            additionalAvailabilityInfo={additionalAvailabilityInfo as AdditionalAvailabilityInfoModel}
                            extendedStyles={styles.productAvailability}
                        />
                    )}
                    {cartLine.properties.availableAltWarehouse && (
                        <Tooltip
                            {...styles.tooltipStyle}
                            // XNG-CHANGES: GSS-984 : hiding other tooltip in the cartline when triggering the altDC
                            onTrigger={() => {
                                hideAll();
                            }}
                            text={
                                siteMessage(
                                    "GUEST_Cart_Alt_DC_Button_Message",
                                    "Available to ship from an alternate DC. Processing fee will be assessed when adding to a cart",
                                ) as string
                            }
                            triggerComponent={
                                <Link
                                    {...styles.altDcLink}
                                    onClick={() => {
                                        if (isOrderConfirmationPage) {
                                            return;
                                        }
                                        setShipFromAltDCModalIsOpen({ modalIsOpen: true, backorder: cartLine });
                                    }}
                                >
                                    {siteMessage("GUEST_Cart_Alt_DC_Button")}
                                </Link>
                            }
                        />
                    )}
                    {cartLine.properties.altWarehouseInfo && (
                        <Tooltip
                            {...styles.tooltipStyle}
                            // XNG-CHANGES: GSS-984 : hiding other tooltip in the cartline when triggering the altDC
                            onTrigger={() => {
                                hideAll();
                            }}
                            text={
                                siteMessage(
                                    "GUEST_Cart_Primary_DC_Button_Message",
                                    "Shipping from the primary DC will change the cart back to have a backorder rail",
                                ) as string
                            }
                            triggerComponent={
                                <Link {...styles.altDcLink} onClick={handleShipFromPrimaryDC}>
                                    {siteMessage("GUEST_Cart_Primary_DC")}
                                </Link>
                            }
                        />
                    )}
                </CartLineContainer.Price>
                <CartLineContainer.Qty>
                    <CartLineQuantity
                        cart={cart}
                        editable={editable}
                        onQtyOrderedChange={qtyOrderedChangeHandler}
                        {...{ showActions: false }}
                    />
                </CartLineContainer.Qty>
                <CartLineContainer.Total>
                    {cartLine.pricing && (showPricing || isCheckoutReviewAndSubmitPage) && (
                        <>
                            {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                <>
                                    <SmallHeadingAndText
                                        heading={`${translate("Total")} (${translate("Ex. VAT")})`}
                                        text={cartLine.pricing.extendedUnitNetPriceDisplay}
                                        extendedStyles={styles.subtotalWithoutVatText}
                                    />
                                    <SmallHeadingAndText
                                        heading={`${translate("Total VAT")} (${cartLine.pricing.vatRate}%)`}
                                        text={cartLine.pricing.vatAmountDisplay}
                                        extendedStyles={styles.vatAmount}
                                    />
                                </>
                            )}
                            <SmallHeadingAndText
                                heading={
                                    !enableVat
                                        ? translate("Total")
                                        : vatPriceDisplay !== "DisplayWithoutVat"
                                        ? `${translate("Total")} (${translate("Inc. VAT")})`
                                        : `${translate("Total")} (${translate("Ex. VAT")})`
                                }
                                text={
                                    enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                        ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                        : cartLine.pricing.extendedUnitNetPriceDisplay
                                }
                                extendedStyles={styles.extendedUnitNetPrice}
                            />
                        </>
                    )}
                </CartLineContainer.Total>
                <CartLineContainer.PoNumber>
                    {postalCode && (
                        <StyledWrapper {...styles.poNumberWrapper}>
                            <SmallHeadingAndText
                                text={postalCode}
                                heading={translate("PO #")}
                                extendedStyles={styles.poNumber}
                            />
                            <Icon src={Stack} size={14} color="text" />
                        </StyledWrapper>
                    )}
                    {editable && (
                        <StyledWrapper {...styles.poContainer}>
                            <Tooltip
                                {...styles.tooltipStyle}
                                trigger="click"
                                offset={[0, 16]}
                                text={
                                    <LoadingOverlay loading={isPoLoading}>
                                        <StyledWrapper {...styles.tippyContentWrapper}>
                                            <Typography as="p" weight="bold">
                                                {translate("Purchase Order #")}
                                            </Typography>
                                            <TextField
                                                label="PO #"
                                                placeholder={translate("99800")}
                                                value={postalCode}
                                                maxLength={25}
                                                onChange={onChangeHandler}
                                                onBlur={() => {
                                                    hideAll();
                                                }}
                                                onKeyDown={onKeyDownHandler}
                                                data-test-selector="PostalCode"
                                            />
                                            <Link
                                                {...styles.applyToAllLink}
                                                onClick={() => applyToAllClickHandler(postalCode)}
                                                disabled={!cartLine.properties["poNumber"]}
                                            >
                                                {translate("Apply To All")}
                                            </Link>
                                            <StyledWrapper {...styles.poListWrapper}>
                                                {Object.keys(poLineList).map((poItem, idx) => (
                                                    <Link
                                                        color="secondary"
                                                        {...styles.poListLink}
                                                        key={idx}
                                                        onClick={() => handlePOLineListClick(poItem)}
                                                    >
                                                        {poLineList[poItem].original}
                                                    </Link>
                                                ))}
                                            </StyledWrapper>
                                            <Link
                                                {...styles.editPurchaseOrderLink}
                                                disabled={!isShowPOEnabled}
                                                onClick={handleEditPOClick}
                                            >
                                                {translate("Edit Purchase Order #")}
                                            </Link>
                                        </StyledWrapper>
                                    </LoadingOverlay>
                                }
                                onHidden={() => {
                                    onBlurHandler();
                                }}
                                triggerComponent={
                                    <StyledWrapper {...styles.moreIconLink}>
                                        <Icon src={More} color="primary" width={35} height={33} />
                                    </StyledWrapper>
                                }
                            />
                        </StyledWrapper>
                    )}
                    <Modal
                        sizeVariant="medium"
                        headline={translate("Edit Purchase Order #")}
                        handleClose={() => {
                            setPoModalOpen(false);
                            if (poTobeRemoved?.length) {
                                setPoLineList({
                                    ...poLineList,
                                    [poLineList[poTobeRemoved].original]: {
                                        ...poLineList[poTobeRemoved],
                                        removed: false,
                                    },
                                });
                            }
                        }}
                        isOpen={pOModalOpen}
                    >
                        <LoadingOverlay loading={isPoModalLoading}>
                            {!showRemoveModal ? (
                                <StyledWrapper>
                                    <StyledWrapper {...styles.poModalWrapper}>
                                        {Object.keys(poLineList).map((poItem, idx) => (
                                            <StyledWrapper key={idx} {...styles.polineWrapper}>
                                                <TextField
                                                    placeholder={translate("99800")}
                                                    value={poLineList[poItem].temp}
                                                    maxLength={25}
                                                    disabled={poLineList[poItem].removed}
                                                    onChange={event => handlePOChange(event, poItem)}
                                                    data-test-selector="PostalCode"
                                                />
                                                <Link
                                                    {...styles.removeLink}
                                                    disabled={poLineList[poItem].removed}
                                                    onClick={() => handleRemoveClick(poItem)}
                                                >
                                                    {translate("Remove")}
                                                </Link>
                                            </StyledWrapper>
                                        ))}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.removePoWrapper}>
                                        <Button variant="secondary" onClick={handleCancelClick}>
                                            {translate("Cancel")}
                                        </Button>
                                        <Button disabled={!isSaveChangesEnabled} onClick={handleSaveChangesClick}>
                                            {translate("Save Changes")}
                                        </Button>
                                    </StyledWrapper>
                                </StyledWrapper>
                            ) : (
                                <StyledWrapper>
                                    <Typography as="p">
                                        {translate(
                                            "Are you sure you want to remove this PO {0}? This action cannot be reverted.",
                                            poTobeRemoved,
                                        )}
                                    </Typography>
                                    <StyledWrapper {...styles.buttonsWrapper}>
                                        <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
                                            {translate("No")}
                                        </Button>
                                        <Button onClick={handleYesClick}>{translate("Yes")}</Button>
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                        </LoadingOverlay>
                    </Modal>
                </CartLineContainer.PoNumber>

                <CartLineContainer.Actions>
                    {cartLine.notes && (
                        <CartLineNotes
                            cart={cart}
                            editable={editable}
                            label={translate("Line Notes")}
                            extendedStyles={styles.cartLineNotes}
                        />
                    )}
                    {cart.showCostCode && cartLine.costCode && (
                        <GridItem {...styles.costCodeGridItem}>
                            <Typography {...styles.costCodeValueText} as="p">
                                <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}:</Typography>
                                {cartLine.costCode}
                            </Typography>
                        </GridItem>
                    )}
                    {errorMessages.map((message, index) => (
                        <Notify
                            {...styles.notify}
                            type="info"
                            data-test-selector="cartline_errorMessage"
                            key={`error_${cartLine.id}`}
                        >
                            {message}
                        </Notify>
                    ))}
                </CartLineContainer.Actions>
            </CartLineContainer>
            {/* <GridContainer
                {...styles.container}
                data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
            >
                <GridItem {...styles.productImageGridItem}>
                    <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                </GridItem>
                <GridItem {...styles.orderLineInfoGridItem}>
                    <GridContainer {...styles.orderLineInfoContainer}>
                        <GridItem {...styles.productDescriptionAndPartNumbersGridItem}>
                            <GridContainer {...styles.productDescriptionAndPartNumbersContainer}>
                                <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                    {cartLine.brand && (
                                        <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                                    )}
                                    <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                                </GridItem>
                                {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                                    <GridItem {...styles.configurationGridItem}>
                                        {cartLine.sectionOptions!.map(option => (
                                            <Typography
                                                {...styles.configurationOptionText}
                                                key={option.sectionOptionId}
                                            >
                                                {`${option.sectionName}:${option.optionName}`}
                                            </Typography>
                                        ))}
                                    </GridItem>
                                )}
                                <GridItem {...styles.productPartNumbersGridItem}>
                                    <ProductPartNumbers
                                        productNumber={cartLine.erpNumber}
                                        customerProductNumber={cartLine.customerName}
                                        manufacturerItem={cartLine.manufacturerItem}
                                        extendedStyles={styles.productPartNumbers}
                                    />
                                </GridItem>
                                {cartLine.isRestricted && (
                                    <GridItem {...styles.cartLineErrorMessageGridItem}>
                                        <Typography
                                            {...styles.cartLineErrorMessageText}
                                            data-test-selector="cartline_restrictedMessage"
                                        >
                                            {translate("Restricted product")}
                                        </Typography>
                                    </GridItem>
                                )}
                                {!cartLine.isActive && (
                                    <GridItem {...styles.cartLineErrorMessageGridItem}>
                                        <Typography {...styles.cartLineErrorMessageText}>
                                            {translate("Inactive Product")}
                                        </Typography>
                                    </GridItem>
                                )}
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.productPriceAndQuantityGridItem}>
                            <GridContainer {...styles.productPriceAndQuantityContainer}>
                                <GridItem {...styles.priceGridItem}>
                                    <ProductPrice
                                        product={cartLine}
                                        showSavings={true}
                                        showSavingsAmount={showSavingsAmount}
                                        showSavingsPercent={showSavingsPercent}
                                        showInvalidPriceMessage={showInvalidPriceMessage}
                                        extendedStyles={styles.price}
                                    />
                                    {promotions?.map(promotion => (
                                        <Typography
                                            {...styles.promotionNameText}
                                            key={promotion.id}
                                            data-test-selector="orderLineCardExpandedPromotionName"
                                        >
                                            {promotion.name}
                                        </Typography>
                                    ))}
                                </GridItem>
                                <GridItem {...styles.quantityGridItem} data-test-selector="orderConformation_Quantity">
                                    <CartLineQuantity
                                        cart={cart}
                                        editable={editable}
                                        extendedStyles={styles.quantity}
                                    />
                                </GridItem>
                                <GridItem {...styles.extendedUnitNetPriceGridItem}>
                                    {cartLine.pricing && (showPricing || isCheckoutReviewAndSubmitPage) && (
                                        <>
                                            {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                <>
                                                    <SmallHeadingAndText
                                                        heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                                        text={cartLine.pricing.extendedUnitNetPriceDisplay}
                                                        extendedStyles={styles.subtotalWithoutVatText}
                                                    />
                                                    <SmallHeadingAndText
                                                        heading={`${translate("Total VAT")} (${
                                                            cartLine.pricing.vatRate
                                                        }%)`}
                                                        text={cartLine.pricing.vatAmountDisplay}
                                                        extendedStyles={styles.vatAmount}
                                                    />
                                                </>
                                            )}
                                            <SmallHeadingAndText
                                                heading={
                                                    !enableVat
                                                        ? translate("Subtotal")
                                                        : vatPriceDisplay !== "DisplayWithoutVat"
                                                        ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                                        : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                                }
                                                text={
                                                    enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                        ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                        : cartLine.pricing.extendedUnitNetPriceDisplay
                                                }
                                                extendedStyles={styles.extendedUnitNetPrice}
                                            />
                                        </>
                                    )}
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        {cartLine.notes && (
                            <CartLineNotes
                                cart={cart}
                                editable={editable}
                                label={translate("Line Notes")}
                                extendedStyles={styles.cartLineNotes}
                            />
                        )}
                        {cart.showCostCode && cartLine.costCode && (
                            <GridItem {...styles.costCodeGridItem}>
                                <Typography {...styles.costCodeValueText} as="p">
                                    <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}:</Typography>
                                    {cartLine.costCode}
                                </Typography>
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
            </GridContainer> */}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
